// @flow

import React from 'react';
import { Bar } from 'react-chartjs-2';
import { I18n, Translate } from 'react-redux-i18n';
import type { CameoGroupsBean } from '../../../../../types/apolloType';
import { CUSTOMER_PROFILES_COLORS, CUSTOMER_PROFILES_TOTAL_COLORS } from '../../../../../const';
import { roundPercentage } from '../../../../../utils/formatUtils';

type Props = {
  data: CameoGroupsBean,
}

const RESPONSIVE_MAX_CHART_WITH = 1024;

function valuesOverBars(ctx, valuesDataset, overlapDataset) {
  ctx.fillStyle = 'black';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'bottom';
  ctx.font = Chart.helpers.fontString(
    10, // font size in px
    Chart.defaults.global.defaultFontStyle,
    Chart.defaults.global.defaultFontFamily,
  );
  const MIN_TEXT_HEIGHT = 15; // minimal height where a text can be placed for this graph
  const TEXT_SPACE = 3; // space between the text and the bar below
  for (let i = 0; i < valuesDataset.data.length; i++) {
    const valuesMetaIndex = Object.keys(valuesDataset._meta)[0];
    const valuesModel = valuesDataset._meta[valuesMetaIndex].data[i]._model;
    const overlapMetaIndex = Object.keys(overlapDataset._meta)[0];
    const overlapModel = overlapDataset._meta[overlapMetaIndex].data[i]._model;
    const y = (valuesModel.y - overlapModel.y > 0 && valuesModel.y - overlapModel.y < MIN_TEXT_HEIGHT + overlapDataset.borderWidth.top)
      ? overlapModel.y - TEXT_SPACE
      : valuesModel.y - TEXT_SPACE;
    ctx.fillText(roundPercentage(valuesDataset.data[i]), valuesModel.x, y);
  }
}

class CustomerProfileGraph extends React.Component<Props, State> {
  getSortedCameoGroupsByShoppingCenter = () =>
    this.props.data.cameoGroupsByShoppingCenter.sort((a, b) => a.categoryId - b.categoryId);

  getPopulationPercentage = () =>
    this.getSortedCameoGroupsByShoppingCenter().map(entry => entry.cameoGroupPercentage);

  getAveragePopulationPercentage = () => {
    const { cameoGroupByCountry } = this.props.data;
    return this.getSortedCameoGroupsByShoppingCenter()
      .map(cameoSc => cameoGroupByCountry
        .find(cameoCountry => cameoCountry.cameoGroupName === cameoSc.cameoGroupName))
      .map(cameoCountry => cameoCountry.cameoGroupPercentage);
  }

  getTotalPercentage = () =>
    this.getSortedCameoGroupsByShoppingCenter()
      .map(entry => this.getTotalPercentageCategory(entry.categoryId));


  getTotalPercentageCategory = (categoryId: number) => this.props.data.cameoGroupsByShoppingCenter
    .filter(entry => entry.categoryId === categoryId)
    .reduce((result, cameoGroup) => result + cameoGroup.cameoGroupPercentage, 0);

  getLabelsCameoGroups = () =>
    this.getSortedCameoGroupsByShoppingCenter().map(entry => entry.cameoGroupName);

  getBarColors = () =>
    this.getSortedCameoGroupsByShoppingCenter()
      .map(entry => CUSTOMER_PROFILES_COLORS[entry.categoryName]);

  getTotalBarColors = () =>
    this.getSortedCameoGroupsByShoppingCenter()
      .map(entry => CUSTOMER_PROFILES_TOTAL_COLORS[entry.categoryName]);

  getMultiLineLabels = () => this.getLabelsCameoGroups().map((label: string) => {
    if (label.length > 30) {
      const labelWords = label.split(' ');
      const lineSeparator = Math.floor(labelWords.length / 2);
      return [
        labelWords.slice(0, lineSeparator).join(' '),
        labelWords.slice(lineSeparator).join(' '),
      ];
    }
    return label;
  })

  render() {
    const categoryNames = this.getSortedCameoGroupsByShoppingCenter()
      .map(cameoGroup => cameoGroup.categoryName);
    const valuesOverTotalBars = (ctx, datasets) => {
      const totalTextColors = this.getTotalBarColors();
      ctx.textAlign = 'left';
      ctx.textBaseline = 'bottom';
      ctx.font = Chart.helpers.fontString(
        20, // font size in px
        'bold', // font style
        Chart.defaults.global.defaultFontFamily,
      );
      datasets.forEach((dataset) => {
        for (let i = 0; i < dataset.data.length; i++) {
          if (ctx.fillStyle !== totalTextColors[i]) {
            // fill text only for the first value of each bar
            const metaIndex = Object.keys(dataset._meta)[0];
            const model = dataset._meta[metaIndex].data[i]._model;
            ctx.fillStyle = totalTextColors[i];
            const alignLeftOffset = model.width / 2;
            ctx.fillText(
              I18n.t('MKT_PAGE.customer_profile.group_name', {
                percentage: roundPercentage(dataset.data[i]),
                categoryName: categoryNames[i],
              }),
              model.x - alignLeftOffset,
              model.y - 5,
            );
          }
        }
      });
    };

    const data = {
      labels: this.getMultiLineLabels(),
      datasets: [
        {
          label: I18n.t('MKT_PAGE.customer_profile.tooltips_graph.country_average'),
          data: this.getAveragePopulationPercentage(),
          stack: 1,
          borderWidth: {
            top: 5, bottom: 0, left: 0, right: 0,
          },
          backgroundColor: 'transparent',
          borderColor: 'black',
          minBarLength: 0,
          barPercentage: 0.9,
          categoryPercentage: 1,
        },
        {
          label: I18n.t('MKT_PAGE.customer_profile.tooltips_graph.total_app_data_profile'),
          data: this.getTotalPercentage(),
          stack: 1,
          borderWidth: {
            top: 5, bottom: 0, left: 0, right: 0,
          },
          backgroundColor: 'transparent',
          borderColor: this.getTotalBarColors(),
          hoverBorderColor: this.getTotalBarColors(),
          minBarLength: 0,
          barPercentage: 1,
          categoryPercentage: 1,
        },
        {
          label: I18n.t('MKT_PAGE.customer_profile.tooltips_graph.app_data_profile'),
          data: this.getPopulationPercentage(),
          backgroundColor: this.getBarColors(),
          stack: 1,
          minBarLength: 0,
          barPercentage: 0.9,
          categoryPercentage: 1,
        },
      ],
    };

    const options = {
      maintainAspectRatio: false,
      responsive: screen.width > RESPONSIVE_MAX_CHART_WITH,
      legend: {
        display: false,
      },
      layout: { padding: 20 },
      scales: {
        xAxes: [
          {
            gridLines: {
              display: false,
              drawBorder: false,
            },
            stacked: true,
          },
        ],
        yAxes: [
          {
            stacked: false,
            ticks: {
              beginAtZero: true,
              callback(value, index, values) {
                if (value === values[values.length - 1]) {
                  return '';
                }
                if (value === values[values.length - 2]) {
                  return `< ${roundPercentage(value)}`;
                }
                return `${roundPercentage(value)}`;
              },
            },
          },
        ],
      },
      tooltips: {
        callbacks: {
          label(context, data2) {
            const value = data2.datasets[context.datasetIndex].data[context.index];
            const labelDisplayed = data2.datasets[context.datasetIndex].label;
            return `${labelDisplayed}: ${roundPercentage(value)}`;
          },
          labelColor(tooltipItem, chart) {
            const dataset = chart.data.datasets[tooltipItem.datasetIndex];
            switch (dataset.label) {
              case I18n.t('MKT_PAGE.customer_profile.tooltips_graph.total_app_data_profile'):
                return {
                  borderColor: 'white',
                  backgroundColor: dataset.borderColor[tooltipItem.index],
                };
              case I18n.t('MKT_PAGE.customer_profile.tooltips_graph.country_average'):
                return {
                  borderColor: 'white',
                  backgroundColor: 'black',
                };
              default:
                return {
                  borderColor: 'white',
                  backgroundColor: dataset.backgroundColor[tooltipItem.index],
                };
            }
          },
        },
      },
    };

    const plugins = [{
      afterDatasetsDraw(chartInstance) {
        valuesOverBars(
          chartInstance.chart.ctx,
          chartInstance.data.datasets.filter(cat => cat.backgroundColor !== 'transparent')[0],
          chartInstance.data.datasets.filter(cat => cat.backgroundColor === 'transparent' && cat.borderColor === 'black')[0],
        );
        valuesOverTotalBars(
          chartInstance.chart.ctx,
          chartInstance.data.datasets.filter(dataset => (
            dataset.label === I18n.t('MKT_PAGE.customer_profile.tooltips_graph.total_app_data_profile')
          )),
        );
      },
    }];

    return (
      <>
        <div className="graph-container">
          <Bar
            height={400}
            width={RESPONSIVE_MAX_CHART_WITH}
            options={options}
            data={data}
            plugins={plugins}
          />
        </div>
        <div className="bargraph-legend-container">
          <div className="bargraph-legend">
            {
              Object.values(CUSTOMER_PROFILES_COLORS).map((color, index) => (
                <i key={index.toString()} className="fas fa-square branch-level" style={{ color }} />
              ))
            }
            <Translate value="MKT_PAGE.customer_profile.legend.app_data_profile" />
          </div>
          <div className="bargraph-legend">
            <i className="fas fa-square average" />
            <Translate value="MKT_PAGE.customer_profile.legend.country_average" />
          </div>
        </div>
      </>
    );
  }
}

export default CustomerProfileGraph;
